import {
  Activity,
  CheckCircle,
  Clock,
  FileText,
  History,
  Home,
  Hourglass,
  Minus,
  TrendingUp,
  UserRound,
  Users,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { adminKeyIndicators, adminQuickLinks } from "../../data/adminDashboard";
import { adminIndicatorRoutes } from "../../navigation/dashboardRoutes";
import { DashboardMetricCard } from "../dashboard/DashboardMetricCard";
import {
  DashboardQuickAccess,
  type DashboardQuickAccessConfig,
} from "../dashboard/DashboardQuickAccess";

const indicatorIcons = {
  file: FileText,
  clock: Clock,
  check: CheckCircle,
  home: Home,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  trending: TrendingUp,
  activity: Activity,
};

const quickLinkIcons = {
  file: FileText,
  home: Home,
  users: Users,
  provider: UserRound,
  conventions: FileText,
  logs: History,
};

const adminMetricConfig = {
  totalInvoices: {
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "rounded-2xl bg-primary text-white shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
  inReview: {
    cardClassName:
      "!border-yellow-400/50 !bg-white hover:!border-yellow-400/65 dark:!border-yellow-500/40 dark:!bg-white",
    iconClassName: "rounded-2xl bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    titleClassName: "font-semibold text-foreground",
  },
  approved: {
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
  admissions: {
    cardClassName:
      "!border-green !bg-green shadow-[0_8px_24px_rgba(184,228,23,0.22)] hover:!brightness-[0.97]",
    iconClassName: "rounded-2xl bg-white/45 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
} as const;

const adminQuickAccessConfig: Record<string, DashboardQuickAccessConfig> = {
  invoices: {
    icon: FileText,
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "bg-primary text-white shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  admissions: {
    icon: Home,
    cardClassName:
      "!border-green !bg-green shadow-[0_8px_24px_rgba(184,228,23,0.22)] hover:!brightness-[0.97]",
    iconClassName: "bg-white/45 text-primary shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  users: {
    icon: Users,
    cardClassName:
      "!border-yellow-400/50 !bg-yellow-400/20 hover:!bg-yellow-400/28 dark:!border-yellow-500/40 dark:!bg-yellow-500/15",
    iconClassName: "bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    labelClassName: "font-semibold text-foreground",
  },
  providers: {
    icon: UserRound,
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "bg-primary text-white shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  conventions: {
    icon: FileText,
    cardClassName:
      "!border-yellow/55 !bg-yellow-soft hover:!border-yellow/70 hover:!brightness-[0.98]",
    iconClassName: "bg-yellow/35 text-primary shadow-sm",
    labelClassName: "font-semibold text-foreground",
  },
  logs: {
    icon: History,
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
};

export function AdminKeyIndicators({
  indicators = adminKeyIndicators,
}: {
  indicators?: Array<(typeof adminKeyIndicators)[number] & { value: number }>;
}) {
  const { t } = useTranslation();
  const labels = t.safecheck.adminDashboard.indicators;
  const footers = t.safecheck.adminDashboard.indicatorFooters;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {indicators.map((indicator) => {
        const Icon = indicatorIcons[indicator.icon];
        const FooterIcon = footerIcons[indicator.footerIcon];
        const label = labels[indicator.id as keyof typeof labels];
        let footer = footers[indicator.footerKey as keyof typeof footers];
        if (indicator.id === "admissions" && indicator.footerKey === "inProgress") {
          footer = footer.replace("{count}", String(indicator.value));
        }
        const config = adminMetricConfig[indicator.id as keyof typeof adminMetricConfig];

        return (
          <DashboardMetricCard
            key={indicator.id}
            title={label}
            value={String(indicator.value)}
            footer={
              <span className="inline-flex items-center gap-1">
                <FooterIcon size={12} />
                {footer}
              </span>
            }
            icon={Icon}
            href={adminIndicatorRoutes[indicator.id as keyof typeof adminIndicatorRoutes]}
            {...config}
          />
        );
      })}
    </div>
  );
}

export function AdminQuickLinks() {
  const { t } = useTranslation();
  const labels = t.safecheck.adminDashboard.quickLinks;

  const items = adminQuickLinks.map((link) => ({
    id: link.id,
    href: link.href,
    label: labels[link.id as keyof typeof labels],
  }));

  return (
    <DashboardQuickAccess
      items={items}
      configById={adminQuickAccessConfig}
      className="md:grid-cols-3 lg:grid-cols-6"
    />
  );
}

export function AdminDashboardSectionLabel({ children }: { children: React.ReactNode }) {
  return <DashboardSectionLabel>{children}</DashboardSectionLabel>;
}
